import React, { useState, useEffect } from 'react';
import { api } from '../../src/services/api-new';

// --- Types ---
interface MedicalRecord {
    id: string;
    date: string;
    type: 'Consultation' | 'Surgery' | 'Vaccination' | 'Lab Result';
    title: string;
    doctor: string;
    notes: string;
    diagnosis?: string;
    treatmentPlan?: string;
    prescription?: string[];
    patient_id?: string;
}

interface PatientRecord {
    id: string;
    name: string;
    type: string; // mapped from species
    breed: string;
    gender: string;
    age: string;
    weight: string;
    owner: string; // mapped from user name
    image: string;
    phone: string;
    tags: string[];
    history: MedicalRecord[];
}

interface VetRecordsProps {
    onBack?: () => void;
    initialPatientId?: string;
    appointmentId?: number;
}

export default function VetRecords({ onBack, initialPatientId, appointmentId }: VetRecordsProps) {
    const [patients, setPatients] = useState<PatientRecord[]>([]);
    const [selectedPatientId, setSelectedPatientId] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'vaccines'>('overview');
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [inventory, setInventory] = useState<any[]>([]);
    const [prescribedProducts, setPrescribedProducts] = useState<any[]>([]);
    const [medSearch, setMedSearch] = useState('');
    const [isMedDropdownOpen, setIsMedDropdownOpen] = useState(false);

    // Modals
    const [isAddPatientOpen, setIsAddPatientOpen] = useState(false);
    const [isConsultModalOpen, setIsConsultModalOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsDataLoading(true);
            try {
                const [patientsData, productsData] = await Promise.all([
                    api.patients.getAll(),
                    api.products.getAll()
                ]);

                // Use internal stock (all products) instead of just checking 'Medication' category
                const safeProductsData = Array.isArray(productsData) ? productsData : (productsData as any)?.products || [];
                // const medications = safeProductsData.filter((p: any) => p.category === 'Medication');
                setInventory(safeProductsData);
                // Map backend data to frontend structure. Ensure patientsData is an array to avoid crashes.
                const safePatientsData = Array.isArray(patientsData) ? patientsData : [];

                const formattedPatients = safePatientsData.map((p: any) => ({
                    ...p,
                    type: p.species || p.type,
                    owner: p.owner || p.owner_name || 'Unknown',
                    history: p.history ? p.history.map((h: any) => ({
                        ...h,
                        treatmentPlan: h.treatment_plan || h.treatmentPlan,
                        prescription: typeof h.prescription === 'string' ? JSON.parse(h.prescription || '[]') : h.prescription
                    })) : []
                }));
                setPatients(formattedPatients);
                if (formattedPatients.length > 0) {
                    // Use initialPatientId if provided and exists
                    const targetId = initialPatientId && formattedPatients.some((p: any) => p.id === initialPatientId)
                        ? initialPatientId
                        : formattedPatients[0].id;
                    setSelectedPatientId(targetId);
                }
            } catch (error) {
                console.error("Failed to fetch patients:", error);
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, []);

    // Derived State
    const activePatient = patients.find(p => p.id === selectedPatientId) || patients[0];
    const filteredList = patients.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(p.id).toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.owner.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleAddConsultation = async (e: React.FormEvent) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const titleInput = form.elements.namedItem('title') as HTMLInputElement;
        const notesInput = form.elements.namedItem('notes') as HTMLTextAreaElement;
        const diagnosisInput = form.elements.namedItem('diagnosis') as HTMLInputElement;
        const planInput = form.elements.namedItem('plan') as HTMLTextAreaElement;


        const newRecordData = {
            date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            type: 'Consultation',
            title: titleInput?.value || 'Medical Consultation',
            doctor: 'Dr. Sarah Mitchell',
            notes: notesInput?.value || '',
            diagnosis: diagnosisInput?.value || '',
            treatment_plan: planInput?.value || '',
            prescription: prescribedProducts.map(p => p.name),
            prescribedProducts: prescribedProducts.map(p => ({ productId: p.id, quantity: 1 }))
        };

        try {
            const result = await api.patients.addRecord(activePatient.id, newRecordData) as any;

            // Mark appointment as completed
            if (appointmentId) {
                await api.appointments.updateStatus(appointmentId, 'MEDICAL_RECORD_COMPLETED');
            }

            // Update local state
            const newRecord = {
                ...result.record,
                treatmentPlan: result.record.treatment_plan,
                prescription: typeof result.record.prescription === 'string' ? JSON.parse(result.record.prescription) : result.record.prescription
            };

            const updatedPatients = patients.map(p => {
                if (p.id === activePatient.id) {
                    return { ...p, history: [newRecord, ...(p.history || [])] };
                }
                return p;
            });

            setPatients(updatedPatients);
            setIsConsultModalOpen(false);
            setActiveTab('history');
        } catch (error) {
            console.error("Failed to add record:", error);
            alert("Failed to add record");
        }
    };

    const handleAddPatient = async (e: React.FormEvent) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const nameInput = form.elements.namedItem('name') as HTMLInputElement;
        const speciesInput = form.elements.namedItem('species') as HTMLInputElement;
        const breedInput = form.elements.namedItem('breed') as HTMLInputElement;
        const genderInput = form.elements.namedItem('gender') as HTMLSelectElement;
        const ageInput = form.elements.namedItem('age') as HTMLInputElement;
        const weightInput = form.elements.namedItem('weight') as HTMLInputElement;
        const ownerInput = form.elements.namedItem('owner') as HTMLInputElement;
        const phoneInput = form.elements.namedItem('phone') as HTMLInputElement;

        const newPatientData = {
            name: nameInput?.value || '',
            species: speciesInput?.value || '',
            breed: breedInput?.value || '',
            gender: genderInput?.value || 'Unknown',
            age: ageInput?.value || '',
            weight: weightInput?.value || '',
            owner_name: ownerInput?.value || '',
            phone: phoneInput?.value || '',
            image: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=200&q=80',
            tags: []
        };

        try {
            const result = await api.patients.create(newPatientData) as any;

            // Update local state
            const newPatient = {
                ...result.patient,
                type: result.patient.species || result.patient.type,
                owner: result.patient.owner_name || result.patient.owner || 'Unknown',
                history: []
            };

            setPatients(prev => [...prev, newPatient]);
            setIsAddPatientOpen(false);
            setSelectedPatientId(newPatient.id);
        } catch (error) {
            console.error("Failed to add patient:", error);
            alert("Failed to add patient");
        }
    };

    if (isDataLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-background-dark">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Accessing Patient Archives...</p>
                </div>
            </div>
        );
    }


    return (
        <div className="flex h-full bg-slate-50 dark:bg-background-dark overflow-hidden">

            {/* SIDEBAR: PATIENT SELECTOR */}
            <div className="w-96 bg-white dark:bg-surface-dark border-r border-gray-100 dark:border-gray-800 flex flex-col shrink-0 z-20 shadow-xl shadow-black/5">
                <div className="p-8 border-b border-gray-100 dark:border-gray-800 space-y-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-1">Clinical Database</p>
                            <h2 className="text-2xl font-black text-primary-dark dark:text-white tracking-tight">Records</h2>
                        </div>
                        <button
                            onClick={() => setIsAddPatientOpen(true)}
                            className="bg-primary hover:bg-primary-dark text-white p-3 rounded-2xl shadow-lg shadow-primary/30 transition-all hover:scale-110 active:scale-95"
                        >
                            <span className="material-icons-round">person_add</span>
                        </button>
                    </div>
                    <div className="relative">
                        <span className="material-icons-round absolute left-4 top-3.5 text-gray-400">search</span>
                        <input
                            type="text"
                            placeholder="ID, Name, or Phone..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-gray-50 dark:bg-background-dark border border-gray-100 dark:border-gray-700 rounded-2xl py-3.5 pl-12 pr-4 text-xs font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all placeholder:text-gray-400"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar no-scrollbar">
                    {filteredList.map(p => (
                        <div
                            key={p.id}
                            onClick={() => setSelectedPatientId(p.id)}
                            className={`p-6 border-b border-gray-50 dark:border-gray-800/50 cursor-pointer transition-all hover:bg-slate-50 dark:hover:bg-black/20 flex gap-4 group ${selectedPatientId === p.id ? 'bg-primary/[0.03] dark:bg-primary/[0.05] border-l-4 border-l-primary' : 'border-l-4 border-l-transparent'}`}
                        >
                            <img src={p.image} className="w-14 h-14 rounded-2xl object-cover bg-gray-100 shadow-md group-hover:scale-105 transition-transform" alt={p.name} />
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start mb-1">
                                    <h3 className={`font-black text-sm truncate ${selectedPatientId === p.id ? 'text-primary' : 'text-gray-900 dark:text-white'}`}>{p.name}</h3>
                                    <span className="text-[8px] font-black text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded uppercase tracking-widest">{p.id}</span>
                                </div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{p.breed} • {p.gender}</p>
                                <p className="text-[10px] font-medium text-gray-500 mt-2 truncate">Owner: <span className="font-bold">{p.owner}</span></p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* MAIN CONTENT Area */}
            <div className="flex-1 flex flex-col overflow-hidden">

                {!activePatient ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-gray-400">
                        <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-3xl flex items-center justify-center mb-6 text-gray-300">
                            <span className="material-icons-round text-4xl">folder_off</span>
                        </div>
                        <h3 className="text-lg font-black text-gray-300 uppercase tracking-widest mb-2">No Record Selected</h3>
                        <p className="text-xs font-medium max-w-xs leading-relaxed">Select a patient from the list or add a new patient to access clinical records.</p>
                        <button
                            onClick={() => setIsAddPatientOpen(true)}
                            className="mt-6 text-primary font-black text-[10px] uppercase tracking-widest hover:underline"
                        >
                            + Create New Record
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Header: Patient Profile Card (Glassmorphism inspired) */}
                        <div className="bg-white dark:bg-surface-dark border-b border-gray-100 dark:border-gray-800 p-8 flex flex-col xl:flex-row xl:items-center justify-between gap-8 shrink-0 z-10 shadow-sm">
                            <div className="flex items-center gap-8">
                                {onBack && (
                                    <button
                                        onClick={onBack}
                                        className="flex items-center justify-center w-10 h-10 rounded-full bg-surface-light dark:bg-background-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm"
                                    >
                                        <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                                    </button>
                                )}
                                <div className="relative group">
                                    <img src={activePatient.image} className="w-28 h-28 rounded-3xl object-cover border-4 border-white dark:border-gray-800 shadow-2xl transition-transform group-hover:scale-105" alt={activePatient.name} />
                                    {activePatient.tags && activePatient.tags.length > 0 && (
                                        <div className="absolute -bottom-2 -right-2 bg-primary text-white p-2 rounded-2xl shadow-xl shadow-primary/40 border-2 border-white dark:border-gray-800">
                                            <span className="material-icons-round text-lg">medical_information</span>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div className="flex items-center gap-4 mb-2">
                                        <h1 className="text-4xl font-black text-primary-dark dark:text-white tracking-tight">{activePatient.name}</h1>
                                        {activePatient.tags && activePatient.tags.map(tag => (
                                            <span key={tag} className="bg-primary/10 text-primary text-[9px] font-black px-3 py-1.5 rounded-full uppercase tracking-[0.1em]">{tag}</span>
                                        ))}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-6 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                                        <div className="flex items-center gap-2"><span className="material-icons-round text-sm text-primary">category</span> {activePatient.breed}</div>
                                        <div className="flex items-center gap-2"><span className="material-icons-round text-sm text-primary">person</span> {activePatient.owner}</div>
                                        <div className="flex items-center gap-2"><span className="material-icons-round text-sm text-primary">phone</span> {activePatient.phone}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <button className="w-12 h-12 rounded-2xl border border-gray-100 dark:border-gray-700 flex items-center justify-center text-gray-400 hover:text-primary hover:border-primary transition-all">
                                    <span className="material-icons-round">print</span>
                                </button>
                                <button
                                    onClick={() => setIsConsultModalOpen(true)}
                                    className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 flex items-center gap-3 transition-all hover:-translate-y-1 active:scale-95"
                                >
                                    <span className="material-icons-round text-lg">add_circle</span> New Consult
                                </button>
                            </div>
                        </div>

                        {/* Navigation Tabs */}
                        <div className="bg-white dark:bg-surface-dark border-b border-gray-100 dark:border-gray-800 px-8 flex gap-8">
                            <TabButton label="Profile Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
                            <TabButton label="Visit History" active={activeTab === 'history'} onClick={() => setActiveTab('history')} count={activePatient.history?.length || 0} />
                            <TabButton label="Medical Logs" active={activeTab === 'vaccines'} onClick={() => setActiveTab('vaccines')} />
                        </div>

                        <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-background-dark p-8 custom-scrollbar">

                            {activeTab === 'overview' && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-fade-in-up">
                                    {/* Vitals & Metrics */}
                                    <div className="bg-white dark:bg-surface-dark p-8 rounded-[2.5rem] shadow-sm border border-gray-100 dark:border-gray-800">
                                        <h3 className="text-sm font-black text-primary-dark dark:text-white uppercase tracking-widest mb-8 flex items-center gap-3">
                                            <span className="w-8 h-8 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center"><span className="material-icons-round text-sm">monitor_heart</span></span>
                                            Patient Vitals
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <VitalBox label="Body Mass" value={activePatient.weight} />
                                            <VitalBox label="Chronology" value={activePatient.age} />
                                            <VitalBox label="Biological" value={activePatient.gender} />
                                            <VitalBox label="Identified" value="STABLE" color="text-green-500" />
                                        </div>
                                    </div>

                                    {/* Recent Health Status */}
                                    <div className="md:col-span-2 bg-white dark:bg-surface-dark p-8 rounded-[2.5rem] shadow-sm border border-gray-100 dark:border-gray-800">
                                        <div className="flex justify-between items-center mb-8">
                                            <h3 className="text-sm font-black text-primary-dark dark:text-white uppercase tracking-widest flex items-center gap-3">
                                                <span className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><span className="material-icons-round text-sm">history_edu</span></span>
                                                Latest Diagnosis
                                            </h3>
                                            <button onClick={() => setActiveTab('history')} className="text-[10px] font-black text-primary hover:underline uppercase tracking-widest">Details</button>
                                        </div>
                                        {activePatient.history && activePatient.history.length > 0 ? (
                                            <div className="relative overflow-hidden bg-primary/[0.02] dark:bg-primary/[0.05] p-8 rounded-[2rem] border border-primary/10">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h4 className="text-lg font-black text-primary-dark dark:text-primary-light">{activePatient.history[0].title}</h4>
                                                    <span className="text-[10px] font-black text-primary/60 bg-white dark:bg-surface-dark px-3 py-1 rounded-full shadow-sm">{activePatient.history[0].date}</span>
                                                </div>
                                                <p className="text-sm text-gray-500 font-medium leading-relaxed italic mb-6">"{activePatient.history[0].notes}"</p>
                                                <div className="flex items-center gap-3 pt-6 border-t border-primary/5">
                                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Attending Vet:</span>
                                                    <span className="text-[10px] font-black text-primary uppercase">{activePatient.history[0].doctor}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="h-40 flex items-center justify-center text-gray-400 text-sm font-bold uppercase tracking-widest italic">No clinical records available</div>
                                        )}
                                    </div>

                                    {/* Documentation & Labs */}
                                    <div className="md:col-span-3 bg-white dark:bg-surface-dark p-8 rounded-[2.5rem] shadow-sm border border-gray-100 dark:border-gray-800">
                                        <h3 className="text-sm font-black text-primary-dark dark:text-white uppercase tracking-widest mb-8 flex items-center gap-3">
                                            <span className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center"><span className="material-icons-round text-sm">science</span></span>
                                            Clinical Diagnostics
                                        </h3>
                                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                                            <FileCard name="Lab_Report_Q4.pdf" date="Oct 2023" type="pdf" />
                                            <FileCard name="X-Ray_Internal.dcm" date="Aug 2023" type="img" />
                                            <button className="border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-3xl flex flex-col items-center justify-center p-6 text-gray-400 hover:border-primary hover:text-primary transition-all group">
                                                <span className="material-icons-round text-4xl mb-2 group-hover:scale-110 transition-transform">cloud_upload</span>
                                                <span className="text-[10px] font-black uppercase tracking-widest">Upload File</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'history' && (
                                <div className="max-w-4xl mx-auto space-y-10 animate-fade-in-up">
                                    {activePatient.history && activePatient.history.map((record, i) => (
                                        <div key={record.id} className="relative pl-12">
                                            {/* Timeline Line */}
                                            {i !== activePatient.history.length - 1 && <div className="absolute left-[23px] top-12 bottom-[-40px] w-1 bg-gradient-to-b from-primary/30 to-transparent"></div>}

                                            <div className="absolute left-0 top-2 w-12 h-12 rounded-2xl bg-white dark:bg-surface-dark shadow-xl flex items-center justify-center z-10 text-primary border border-gray-50 dark:border-gray-800">
                                                <span className="material-icons-round">
                                                    {record.type === 'Consultation' ? 'medical_information' : 'vaccines'}
                                                </span>
                                            </div>

                                            <div className="bg-white dark:bg-surface-dark rounded-[2.5rem] border border-gray-100 dark:border-gray-800 p-8 shadow-sm hover:shadow-xl transition-all group">
                                                <div className="flex flex-col md:flex-row justify-between md:items-start gap-4 mb-8">
                                                    <div>
                                                        <h3 className="text-xl font-black text-primary-dark dark:text-white tracking-tight mb-1">{record.title}</h3>
                                                        <div className="flex items-center gap-3">
                                                            <span className="bg-primary/5 text-primary text-[9px] font-black px-2 py-0.5 rounded tracking-widest uppercase">{record.type}</span>
                                                            <span className="text-[10px] font-bold text-gray-400">Dr. {record.doctor}</span>
                                                        </div>
                                                    </div>
                                                    <span className="text-[11px] font-black text-gray-400 bg-gray-50 dark:bg-gray-800 px-4 py-1.5 rounded-full uppercase tracking-tighter self-start">{record.date}</span>
                                                </div>

                                                <div className="space-y-8">
                                                    <div>
                                                        <h4 className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-3">Clinical Assessment</h4>
                                                        <p className="text-sm text-gray-600 dark:text-gray-300 font-medium leading-relaxed">{record.notes}</p>
                                                    </div>

                                                    {record.diagnosis && (
                                                        <div className="p-6 bg-red-500/5 dark:bg-red-500/10 rounded-3xl border border-red-500/10">
                                                            <h4 className="text-[10px] font-black text-red-500 uppercase tracking-widest mb-2">Final Diagnosis</h4>
                                                            <p className="text-sm font-black text-gray-800 dark:text-white uppercase">{record.diagnosis}</p>
                                                        </div>
                                                    )}

                                                    {record.prescription && record.prescription.length > 0 && (
                                                        <div>
                                                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Prescribed Protocol</h4>
                                                            <div className="flex flex-wrap gap-3">
                                                                {record.prescription.map((rx, idx) => (
                                                                    <span key={idx} className="flex items-center gap-2 bg-white dark:bg-gray-800 text-primary-dark dark:text-primary-light px-4 py-2 rounded-2xl text-[11px] font-black shadow-sm border border-gray-100 dark:border-gray-700">
                                                                        <span className="material-icons-round text-sm">medication</span> {rx}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* MODAL: NEW CONSULTATION */}
            {isConsultModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-primary-dark/40 backdrop-blur-xl animate-fade-in" onClick={() => setIsConsultModalOpen(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up" onClick={e => e.stopPropagation()}>
                        <div className="p-10 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-1">New Entry</p>
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Clinical Consultation</h2>
                            </div>
                            <button onClick={() => setIsConsultModalOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-center text-gray-400"><span className="material-icons-round">close</span></button>
                        </div>

                        <form id="consultationForm" onSubmit={handleAddConsultation} className="flex-1 overflow-y-auto p-10 space-y-10 custom-scrollbar">
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Consultation Title</label>
                                <input name="title" required type="text" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-lg font-black outline-none transition-all placeholder:text-gray-300" placeholder="e.g. Annual Respiratory Screen" />
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">SOAP Clinical Notes</label>
                                <textarea name="notes" required rows={4} className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-6 rounded-3xl text-sm font-medium outline-none transition-all resize-none" placeholder="Enter clinical assessment..."></textarea>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Medical Diagnosis</label>
                                    <input name="diagnosis" type="text" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-xs font-black outline-none transition-all" placeholder="Enter findings..." />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Treatment Plan</label>
                                    <input name="plan" type="text" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-xs font-black outline-none transition-all" placeholder="Next steps..." />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Prescriptions (Select from Inventory)</label>
                                <div className="space-y-3 mb-4">
                                    {prescribedProducts.map(p => (
                                        <div key={p.id} className="bg-slate-50 dark:bg-background-dark/50 border border-gray-100 dark:border-gray-800 p-3 rounded-2xl flex items-center justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="text-[11px] font-black text-primary-dark dark:text-white">{p.name}</div>
                                                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{p.category}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label className="text-[9px] font-bold text-gray-400 uppercase">Qty</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={p.stock}
                                                    value={p.quantity || 1}
                                                    onChange={(e) => {
                                                        const newQty = parseInt(e.target.value) || 1;
                                                        setPrescribedProducts(prev => prev.map(item =>
                                                            item.id === p.id ? { ...item, quantity: newQty } : item
                                                        ));
                                                    }}
                                                    className="w-16 bg-white dark:bg-black/20 border border-transparent focus:border-primary/30 p-2 rounded-xl text-center text-xs font-black outline-none transition-all"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setPrescribedProducts(prev => prev.filter(item => item.id !== p.id))}
                                                className="w-8 h-8 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors flex items-center justify-center"
                                            >
                                                <span className="material-icons-round text-sm">delete</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="relative">
                                    <div className="relative">
                                        <span className="material-icons-round absolute left-4 top-3.5 text-gray-400">search</span>
                                        <input
                                            type="text"
                                            value={medSearch}
                                            onFocus={() => setIsMedDropdownOpen(true)}
                                            onChange={(e) => {
                                                setMedSearch(e.target.value);
                                                setIsMedDropdownOpen(true);
                                            }}
                                            placeholder="Search or select medication..."
                                            className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-3.5 pl-12 pr-12 rounded-2xl text-xs font-black outline-none transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setIsMedDropdownOpen(!isMedDropdownOpen)}
                                            className="absolute right-4 top-3.5 text-gray-400 hover:text-primary transition-colors"
                                        >
                                            <span className="material-icons-round">{isMedDropdownOpen ? 'expand_less' : 'expand_more'}</span>
                                        </button>
                                    </div>

                                    {isMedDropdownOpen && (
                                        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-surface-dark rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 max-h-60 overflow-y-auto z-20 custom-scrollbar">
                                            {inventory
                                                .filter(p => p.name.toLowerCase().includes(medSearch.toLowerCase()) && !prescribedProducts.some(sel => sel.id === p.id))
                                                .map(p => (
                                                    <button
                                                        key={p.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setPrescribedProducts(prev => [...prev, { ...p, quantity: 1 }]);
                                                            setMedSearch('');
                                                            setIsMedDropdownOpen(false);
                                                        }}
                                                        className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800 flex justify-between items-center transition-colors border-b border-gray-50 dark:border-gray-800/50 last:border-0"
                                                    >
                                                        <div>
                                                            <div className="text-[11px] font-bold text-gray-700 dark:text-gray-200">{p.name}</div>
                                                            <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{p.category}</div>
                                                        </div>
                                                        <span className={`text-[9px] font-black px-2 py-0.5 rounded ${p.stock > 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                                            {p.stock} units
                                                        </span>
                                                    </button>
                                                ))}
                                            {inventory.filter(p => p.name.toLowerCase().includes(medSearch.toLowerCase()) && !prescribedProducts.some(sel => sel.id === p.id)).length === 0 && (
                                                <div className="p-4 text-center text-[10px] text-gray-400 font-bold italic">No item found</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </form>

                        <div className="p-10 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 bg-slate-50/50 dark:bg-black/20">
                            <button type="button" onClick={() => setIsConsultModalOpen(false)} className="px-8 py-4 font-black text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">Discard Draft</button>
                            <button form="consultationForm" type="submit" className="bg-primary text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 transition-all hover:-translate-y-1 active:scale-95">Save Final Record</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: ADD NEW PATIENT */}
            {isAddPatientOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-primary-dark/40 backdrop-blur-xl animate-fade-in" onClick={() => setIsAddPatientOpen(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up" onClick={e => e.stopPropagation()}>
                        <div className="p-10 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-1">New Entry</p>
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Add New Patient</h2>
                            </div>
                            <button onClick={() => setIsAddPatientOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-center text-gray-400"><span className="material-icons-round">close</span></button>
                        </div>

                        <form id="addPatientForm" onSubmit={handleAddPatient} className="flex-1 overflow-y-auto p-10 space-y-8 custom-scrollbar">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Patient Name</label>
                                    <input
                                        name="name"
                                        required
                                        type="text"
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-lg font-black outline-none transition-all placeholder:text-gray-300"
                                        placeholder="e.g. Max, Bella, Charlie"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Species</label>
                                    <input
                                        name="species"
                                        required
                                        type="text"
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all placeholder:text-gray-300"
                                        placeholder="e.g. Dog, Cat, Rabbit"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Breed</label>
                                    <input
                                        name="breed"
                                        required
                                        type="text"
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all placeholder:text-gray-300"
                                        placeholder="e.g. Golden Retriever, Persian"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Gender</label>
                                    <select
                                        name="gender"
                                        required
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                    >
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Unknown">Unknown</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Age</label>
                                    <input
                                        name="age"
                                        required
                                        type="text"
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all placeholder:text-gray-300"
                                        placeholder="e.g. 2 years, 6 months"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Weight</label>
                                    <input
                                        name="weight"
                                        required
                                        type="text"
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all placeholder:text-gray-300"
                                        placeholder="e.g. 25 kg, 5 lbs"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Owner Name</label>
                                    <input
                                        name="owner"
                                        required
                                        type="text"
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all placeholder:text-gray-300"
                                        placeholder="Owner's full name"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Phone Number</label>
                                    <input
                                        name="phone"
                                        required
                                        type="tel"
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all placeholder:text-gray-300"
                                        placeholder="+1 (555) 123-4567"
                                    />
                                </div>
                            </div>
                        </form>

                        <div className="p-10 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 bg-slate-50/50 dark:bg-black/20">
                            <button type="button" onClick={() => setIsAddPatientOpen(false)} className="px-8 py-4 font-black text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">Cancel</button>
                            <button form="addPatientForm" type="submit" className="bg-primary text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 transition-all hover:-translate-y-1 active:scale-95">Add Patient</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

// --- Sub-components ---
const TabButton = ({ label, active, onClick, count }: any) => (
    <button
        onClick={onClick}
        className={`py-6 text-[11px] font-black uppercase tracking-[0.2em] border-b-4 transition-all flex items-center gap-3 ${active ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
    >
        {label}
        {count !== undefined && <span className={`text-[9px] px-2.5 py-0.5 rounded-full shadow-sm ${active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'}`}>{count}</span>}
    </button>
);

const VitalBox = ({ label, value, color }: any) => (
    <div className="bg-slate-50 dark:bg-background-dark/50 p-5 rounded-3xl border border-gray-50 dark:border-gray-800">
        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">{label}</p>
        <p className={`text-sm font-black ${color || 'text-primary-dark dark:text-white'} uppercase`}>{value}</p>
    </div>
);

const FileCard = ({ name, date, type }: any) => (
    <div className="bg-slate-50 dark:bg-background-dark/50 p-6 rounded-[2rem] border border-gray-50 dark:border-gray-800 flex flex-col items-center justify-center text-center group cursor-pointer hover:border-primary/50 transition-all hover:shadow-lg">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform ${type === 'pdf' ? 'bg-red-500/10 text-red-500' : 'bg-blue-500/10 text-blue-500'}`}>
            <span className="material-icons-round text-2xl">{type === 'pdf' ? 'picture_as_pdf' : 'insights'}</span>
        </div>
        <p className="text-[10px] font-black text-primary-dark dark:text-white truncate w-full mb-1">{name}</p>
        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{date}</p>
    </div>
);

